import React from 'react';
import { ChevronDown, ChevronUp, Eye, EyeOff, Pencil } from 'lucide-react';
import { formatApiError } from '../../utils/apiError';
import {
  MODULE_ACCESS_LEVELS,
  type ModuleAccessLevel,
} from '../../config/moduleAccessPolicy';

export function Field({
  label,
  children,
  hint,
  hoverHint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
  /** Shown in a small tooltip when the user hovers or focuses the field control. */
  hoverHint?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="text-gray-600">{label}</span>
      {hint ? <span className="block text-xs text-gray-500 mt-0.5">{hint}</span> : null}
      <div className="mt-1">
        <div className="group/field relative">
          {children}
          {hoverHint ? (
            <p
              className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-max max-w-xs rounded-md border border-primary-200 bg-primary-50 px-2.5 py-1.5 text-xs leading-snug text-primary-900 shadow-sm group-hover/field:block group-focus-within/field:block"
              role="tooltip"
            >
              {hoverHint}
            </p>
          ) : null}
        </div>
      </div>
    </label>
  );
}

export function inputClass() {
  return 'royal-input w-full px-3 py-2 text-sm text-secondary-900';
}

export function StatusBanner({ error, success }: { error?: string | null; success?: string | null }) {
  if (!error && !success) return null;
  return (
    <div
      className={`rounded-md px-3 py-2 text-sm ${
        error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-800 border border-green-200'
      }`}
    >
      {error || success}
    </div>
  );
}

export function SimpleTable({
  columns,
  rows,
}: {
  columns: { key: string; label: string; render?: (row: Record<string, unknown>) => React.ReactNode }[];
  rows: Record<string, unknown>[];
}) {
  if (!rows.length) return <p className="text-sm text-secondary-500">No records yet.</p>;
  return (
    <div className="overflow-auto max-h-72 royal-card rounded-lg">
      <table className="royal-table min-w-full text-xs">
        <thead className="sticky top-0">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="px-3 py-2 text-left">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-royal-ivory">
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-primary-500/10">
              {columns.map((c) => (
                <td key={c.key} className="px-2 py-1 whitespace-nowrap">
                  {c.render ? c.render(row) : String(row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function parseApiError(err: unknown, fallback: string): string {
  return formatApiError(err, fallback);
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Choose an option…',
  searchPlaceholder = 'Type to search…',
  emptyLabel = 'No matches',
  disabled,
  required,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; hint?: string }>;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  required?: boolean;
}) {
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query) ||
        (option.hint ?? '').toLowerCase().includes(query)
    );
  }, [options, search]);

  return (
    <div className="space-y-2">
      <input
        type="search"
        className={inputClass()}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={searchPlaceholder}
        disabled={disabled}
        aria-label="Search options"
      />
      <select
        className={inputClass()}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
      >
        <option value="">{placeholder}</option>
        {filtered.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
            {option.hint ? ` — ${option.hint}` : ''}
          </option>
        ))}
      </select>
      <p className="text-xs text-secondary-500">
        {filtered.length} of {options.length} shown
        {search.trim() ? ` · matching "${search.trim()}"` : ''}
      </p>
      {!filtered.length ? <p className="text-xs text-secondary-400">{emptyLabel}</p> : null}
    </div>
  );
}

export function ExpandableModuleShell({
  expanded,
  onToggle,
  title,
  headerActions,
  children,
  className = 'text-sm',
  headerClassName = 'flex flex-wrap items-center justify-between gap-4 px-4 py-3',
  bodyClassName = 'border-t border-primary-500/10 bg-secondary-50/40 divide-y divide-primary-500/10',
  titleButtonClassName = 'inline-flex items-center gap-2 font-medium text-secondary-900 min-w-[8rem] text-left hover:text-primary-700',
  expandable = true,
  chevronClassName = 'h-4 w-4 shrink-0',
}: {
  expanded: boolean;
  onToggle: () => void;
  title: React.ReactNode;
  headerActions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  titleButtonClassName?: string;
  expandable?: boolean;
  chevronClassName?: string;
}) {
  const hasBody = expanded && React.Children.count(children) > 0;
  return (
    <div className={className}>
      <div className={headerClassName}>
        <button type="button" onClick={onToggle} className={titleButtonClassName}>
          {expandable ? (
            expanded ? (
              <ChevronUp className={chevronClassName} aria-hidden />
            ) : (
              <ChevronDown className={chevronClassName} aria-hidden />
            )
          ) : (
            <span className="w-4 shrink-0" aria-hidden />
          )}
          {title}
        </button>
        {headerActions}
      </div>
      {hasBody ? <div className={bodyClassName}>{children}</div> : null}
    </div>
  );
}

export function AccessLevelPicker({
  value,
  supportsWrite,
  disabled,
  onChange,
}: {
  value: ModuleAccessLevel;
  supportsWrite: boolean;
  disabled?: boolean;
  onChange: (level: ModuleAccessLevel) => void;
}) {
  const levels = MODULE_ACCESS_LEVELS.filter((level) => level.value !== 'write' || supportsWrite);

  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Access level">
      {levels.map((level) => {
        const active = value === level.value;
        const Icon = level.value === 'none' ? EyeOff : level.value === 'read' ? Eye : Pencil;
        return (
          <button
            key={level.value}
            type="button"
            disabled={disabled}
            title={level.description}
            onClick={() => onChange(level.value)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 ${
              active
                ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                : 'border-secondary-200 bg-white text-secondary-700 hover:border-primary-300 hover:bg-primary-50'
            }`}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {level.shortLabel}
          </button>
        );
      })}
    </div>
  );
}

export function ModuleShell({
  title,
  icon: Icon,
  onRefresh,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  onRefresh: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="p-6 space-y-5">
      <div className="royal-page-header flex items-center justify-between">
        <h2 className="royal-page-title flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500/15 border border-primary-500/25">
            <Icon className="h-5 w-5 text-primary-700" />
          </span>
          {title}
        </h2>
        <button type="button" onClick={onRefresh} className="royal-btn-secondary inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm">
          <i className="fas fa-sync-alt text-primary-600 text-xs" aria-hidden />
          Refresh
        </button>
      </div>
      {children}
    </div>
  );
}

export function TabBar({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 border-b border-primary-500/20 pb-2">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            active === t.id
              ? 'royal-btn-primary shadow-sm'
              : 'royal-btn-secondary text-secondary-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
